import type { AquaActionReceipt, AquaOperator, AquaScreenContext, AquaView } from "@/lib/aqua/types";

const waitForRender = () => new Promise<void>((resolve) => {
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  else setTimeout(resolve, 0);
});

function receipt(input: Omit<AquaActionReceipt, "timestamp">): AquaActionReceipt {
  return { ...input, timestamp: new Date().toISOString() };
}

const portalTools = new Set([
  "inspect_current_screen",
  "open_client",
  "open_project_address",
  "open_sow_category",
  "open_invoice",
  "open_request_review",
  "highlight_registered_control",
  "teach_current_step",
  "navigate_history",
  "scroll_screen",
  "activate_registered_control",
  "fill_registered_field",
  "press_enter",
]);

function visibleControl(context: AquaScreenContext, id: unknown) {
  return context.visibleControls.find((control) => control.id === String(id ?? ""));
}

function writableField(context: AquaScreenContext, id: unknown) {
  return context.writableFields.find((field) => field.id === String(id ?? ""));
}

export function validateSemanticTarget(context: AquaScreenContext, toolName: string, args: Record<string, unknown>): string | null {
  if (context.portalMode && !portalTools.has(toolName)) return "Owner controls are unavailable in the client portal.";
  if (toolName === "open_client" && !context.records.clients.some((client) => client.id === args.client_id)) return "Client identifier is not available.";
  if (toolName === "open_project_address" && !context.records.jobs.some((job) => job.id === args.job_id)) return "Project address identifier is not available.";
  if (toolName === "open_sow_category" && !context.records.sowItems.some((sow) => sow.id === args.sow_id)) return "SOW identifier is not available.";
  if (toolName === "open_invoice" && !context.records.invoices.some((invoice) => invoice.requestId === args.request_id)) return "Invoice identifier is not available.";
  if (toolName === "open_request_review" && !context.records.draws.some((draw) => draw.requestId === args.request_id && draw.status === "CLIENT_REVIEW")) return "No pending request review is available for that identifier.";
  if (toolName === "create_project_address" && !context.records.clients.some((client) => client.id === args.client_id)) return "Client identifier is not available.";
  if (toolName === "create_sow_category" && !context.records.jobs.some((job) => job.id === args.job_id)) return "Project address identifier is not available.";
  if (toolName === "create_draw_request" && !context.records.sowItems.some((sow) => sow.id === args.sow_id)) return "SOW identifier is not available.";
  if (toolName === "mark_check_received" && !context.records.draws.some((draw) => draw.id === args.draw_id)) return "Draw identifier is not available.";
  if (toolName === "delete_record") {
    const type = String(args.record_type ?? "");
    const id = String(args.record_id ?? "");
    const exists = type === "client" ? context.records.clients.some((item) => item.id === id)
      : type === "job" ? context.records.jobs.some((item) => item.id === id)
        : type === "sow" ? context.records.sowItems.some((item) => item.id === id)
          : type === "draw" ? context.records.draws.some((item) => item.id === id) : false;
    if (!exists) return "Record identifier is not available.";
  }
  if (toolName === "highlight_registered_control" && !context.registeredControls.includes(String(args.control_id ?? ""))) return "Control is not registered.";
  if (toolName === "teach_current_step" && !visibleControl(context, args.control_id)) return "Teaching control is not visible and registered on this screen.";
  if (toolName === "activate_registered_control" && !visibleControl(context, args.control_id)) return "Control is not visible and registered on this screen.";
  if (toolName === "fill_registered_field" && !writableField(context, args.field_id)) return "Field is not visible and registered on this screen.";
  if (toolName === "press_enter" && args.field_id && !writableField(context, args.field_id)) return "Field is not visible and registered on this screen.";
  if (toolName === "navigate_workspace" && !["dashboard", "clients", "actions", "settings", "diagnostics"].includes(String(args.destination ?? ""))) return "Destination is not registered.";
  return null;
}

function failed(prior: AquaScreenContext, action: string, target: string, message: string) {
  return receipt({ ok: false, action, target, priorView: prior.view, resultingView: prior.view, verified: false, message });
}

async function confirmed(operator: AquaOperator, prior: AquaScreenContext, action: string, target: string, summary: string) {
  const approved = await operator.confirm(summary);
  return approved ? null : failed(prior, action, target, "The user cancelled the confirmation; nothing changed.");
}

export async function executeAquaTool(operator: AquaOperator, toolName: string, args: Record<string, unknown>): Promise<AquaActionReceipt | AquaScreenContext> {
  const prior = operator.getContext();
  if (toolName === "inspect_current_screen") return prior;
  const validationError = validateSemanticTarget(prior, toolName, args);
  if (validationError) return failed(prior, toolName, "unresolved", validationError);

  let target = "";
  let expectedView: AquaView = prior.view;
  let verify: (context: AquaScreenContext) => boolean = () => true;

  switch (toolName) {
    case "navigate_workspace":
      expectedView = String(args.destination) as AquaView;
      target = expectedView;
      operator.navigate(expectedView as "dashboard" | "clients" | "actions" | "settings" | "diagnostics");
      verify = (context) => context.view === expectedView;
      break;
    case "open_client":
      expectedView = "client"; target = String(args.client_id); operator.openClient(target);
      verify = (context) => context.view === "client" && context.selectedClientId === target;
      break;
    case "open_project_address":
      expectedView = "job"; target = String(args.job_id); operator.openJob(target);
      verify = (context) => context.view === "job" && context.selectedJobId === target;
      break;
    case "open_sow_category":
      expectedView = "sow"; target = String(args.sow_id); operator.openSow(target);
      verify = (context) => context.view === "sow" && context.selectedSowId === target;
      break;
    case "open_invoice":
      expectedView = "invoices"; target = String(args.request_id); operator.openInvoice(target);
      verify = (context) => context.view === "invoices" && context.selectedInvoiceRequestId === target;
      break;
    case "open_request_review":
      expectedView = "sow"; target = String(args.request_id); operator.openRequestReview(target);
      verify = (context) => context.view === "sow" && context.selectedInvoiceRequestId === target;
      break;
    case "highlight_registered_control": {
      target = String(args.control_id);
      const ok = operator.highlight(target);
      return receipt({ ok, action: toolName, target, priorView: prior.view, resultingView: prior.view, verified: ok, message: ok ? "Registered control highlighted." : "Registered control is not currently visible." });
    }
    case "teach_current_step": {
      target = String(args.control_id);
      const control = visibleControl(prior, target)!;
      const ok = operator.highlight(target);
      const purpose = control.teachingReason || `This control is used to ${control.label.toLowerCase()}.`;
      const expected = control.expectedResult || "After it is activated, Aqua will inspect the screen again before continuing.";
      return receipt({
        ok,
        action: toolName,
        target,
        priorView: prior.view,
        resultingView: prior.view,
        verified: ok,
        message: ok ? `${control.label}. Why: ${purpose} What should appear next: ${expected}` : "The exact teaching control is not currently visible.",
      });
    }
    case "navigate_history": {
      target = String(args.direction);
      const ok = operator.history(target as "back" | "forward");
      await waitForRender();
      const resulting = operator.getContext();
      return receipt({ ok, action: toolName, target, priorView: prior.view, resultingView: resulting.view, verified: ok, message: ok ? "Navigation history moved successfully." : "No verified page exists in that direction." });
    }
    case "scroll_screen": {
      target = String(args.direction);
      const ok = operator.scroll(target as "up" | "down" | "top" | "bottom");
      return receipt({ ok, action: toolName, target, priorView: prior.view, resultingView: prior.view, verified: ok, message: ok ? "Screen scroll command executed." : "The screen could not be scrolled." });
    }
    case "activate_registered_control": {
      target = String(args.control_id);
      const control = visibleControl(prior, target)!;
      if (control.consequence !== "safe") {
        const cancelled = await confirmed(operator, prior, toolName, target, `Aqua will ${control.label}. Do you want to continue?`);
        if (cancelled) return cancelled;
      }
      const ok = await operator.activate(target);
      await waitForRender();
      const resulting = operator.getContext();
      return receipt({ ok, action: toolName, target, priorView: prior.view, resultingView: resulting.view, verified: ok, message: ok ? "Registered control activated after authorization." : "The registered control did not activate." });
    }
    case "fill_registered_field": {
      target = String(args.field_id);
      const ok = operator.fillField(target, String(args.value ?? ""));
      return receipt({ ok, action: toolName, target, priorView: prior.view, resultingView: prior.view, verified: ok, message: ok ? "Visible registered field filled." : "The visible field rejected the value." });
    }
    case "press_enter": {
      target = String(args.field_id ?? "focused-field");
      const ok = await operator.pressEnter(args.field_id ? String(args.field_id) : undefined);
      await waitForRender();
      const resulting = operator.getContext();
      return receipt({ ok, action: toolName, target, priorView: prior.view, resultingView: resulting.view, verified: ok, message: ok ? "Enter was sent to the registered field." : "No registered field accepted Enter." });
    }
    case "create_client_and_project": {
      target = String(args.name);
      const created = operator.createClientAndProject({ name: target, company: String(args.company ?? ""), email: String(args.email), phone: String(args.phone), address: String(args.address), title: String(args.title) });
      expectedView = "client"; target = created.clientId;
      verify = (context) => context.view === "client" && context.records.clients.some((item) => item.id === created.clientId) && context.records.jobs.some((item) => item.id === created.jobId);
      break;
    }
    case "create_project_address": {
      const created = operator.createJob({ clientId: String(args.client_id), address: String(args.address), title: String(args.title) });
      expectedView = "job"; target = created.jobId;
      verify = (context) => context.view === "job" && context.records.jobs.some((item) => item.id === created.jobId);
      break;
    }
    case "create_sow_category": {
      const amount = Number(args.contract_amount);
      if (!Number.isFinite(amount) || amount <= 0) return failed(prior, toolName, String(args.job_id), "Contract amount must be greater than zero.");
      const created = operator.createSow({ jobId: String(args.job_id), title: String(args.title), description: String(args.description), contractAmount: amount, approvedBudgetChanges: 0, approvedChangeReason: "" });
      expectedView = "sow"; target = created.sowId;
      verify = (context) => context.view === "sow" && context.records.sowItems.some((item) => item.id === created.sowId);
      break;
    }
    case "create_draw_request": {
      const amount = Number(args.amount);
      target = String(args.sow_id);
      if (!Number.isFinite(amount) || amount <= 0) return failed(prior, toolName, target, "Draw amount must be greater than zero.");
      const cancelled = await confirmed(operator, prior, toolName, target, `Submit a ${amount.toLocaleString("en-US", { style: "currency", currency: "USD" })} draw request for client review?`);
      if (cancelled) return cancelled;
      const created = operator.createDraw({ sowId: target, amount, explanation: String(args.explanation) });
      target = created.drawId;
      verify = (context) => context.records.draws.some((item) => item.id === created.drawId && item.status === "CLIENT_REVIEW");
      break;
    }
    case "mark_check_received": {
      target = String(args.draw_id);
      const draw = prior.records.draws.find((item) => item.id === target);
      if (draw?.status !== "APPROVED") return failed(prior, toolName, target, "Only an approved draw can be marked received.");
      const cancelled = await confirmed(operator, prior, toolName, target, `Confirm the ${draw.amount.toLocaleString("en-US", { style: "currency", currency: "USD" })} check was received?`);
      if (cancelled) return cancelled;
      operator.receivePayment(target);
      verify = (context) => context.records.draws.some((item) => item.id === target && item.status === "RECEIVED");
      break;
    }
    case "delete_record": {
      const recordType = String(args.record_type) as "client" | "job" | "sow" | "draw";
      target = String(args.record_id);
      const cancelled = await confirmed(operator, prior, toolName, target, `Permanently delete this ${recordType} and its governed child records?`);
      if (cancelled) return cancelled;
      operator.deleteRecord({ recordType, id: target });
      verify = (context) => recordType === "client" ? !context.records.clients.some((item) => item.id === target)
        : recordType === "job" ? !context.records.jobs.some((item) => item.id === target)
          : recordType === "sow" ? !context.records.sowItems.some((item) => item.id === target)
            : !context.records.draws.some((item) => item.id === target);
      break;
    }
    default:
      return failed(prior, toolName, "unsupported", "That Aqua Draw capability is not registered.");
  }

  await waitForRender();
  const resulting = operator.getContext();
  const verified = verify(resulting);
  return receipt({
    ok: verified,
    action: toolName,
    target,
    priorView: prior.view,
    resultingView: resulting.view,
    verified,
    message: verified ? "Action executed and the resulting Aqua Draw state was verified." : "The requested action did not reach the expected verified state.",
  });
}
